import { type Transformer } from '@atlaskit/editor-common/types';
import { type EditorActions } from '@atlaskit/editor-core';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { Node } from '@atlaskit/editor-prosemirror/model';
import { type EditorState } from '@atlaskit/editor-prosemirror/state';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';

import { VerticalTextAlignment } from '@canvas/adf';

const transformer = new JSONTransformer();
function toJSON(node: Node): JSONDocNode {
  return transformer.encode(node);
}

function isEmptyProseMirrorDocument(node: Node) {
  return (
    node.childCount === 0 || (node.childCount === 1 && node.firstChild?.childCount === 0)
  );
}

/**
 * Copied from the labs editor for consistent behaviour
 */
export function getEditorValue(
  editorView: EditorView,
  transformer?: Transformer<any>,
): JSONDocNode {
  const doc = editorView.state.doc;
  const encodedDoc =
    transformer && transformer.encode
      ? transformer.encode(Node.fromJSON(editorView.state.schema, doc))
      : doc;
  return toJSON(encodedDoc);
}

/**
 * Needed to compute the position of links when a document is ready.
 * @deprecated
 * @description NOTE: Used only for the purpose of synchronising hyperlink
 * keyboard events with the correct hyperlink in our ECS state. This will be
 * removed once the Editor team provides a more rich hyperlink API.
 */
export function getProsemirrorDocFromActions(actions: EditorActions<any>) {
  // eslint-disable-next-line no-underscore-dangle
  const view = actions._privateGetEditorView();
  const doc = view?.state.doc;
  return doc;
}

export function applyInitialStrongMark(actions: EditorActions<any>) {
  // eslint-disable-next-line no-underscore-dangle
  const view = actions._privateGetEditorView();
  const strongMark = view?.state.schema.marks.strong;

  if (!view || !strongMark || !isEmptyProseMirrorDocument(view.state.doc)) {
    return;
  }

  const hasStrongStoredMark =
    view.state.storedMarks?.some((mark) => mark.type === strongMark) ?? false;

  if (hasStrongStoredMark) {
    return;
  }

  view.dispatch(
    view.state.tr.addStoredMark(strongMark.create()).setMeta('addToHistory', false),
  );
}

function traverseLinks(node: Node, linkMarks: number[], pos = 0) {
  if (node.isText) {
    const link = node.marks.find((mark) => mark.type.name === 'link');
    if (link) {
      linkMarks.push(pos);
    }
  } else {
    node.forEach((child, offset) => traverseLinks(child, linkMarks, pos + offset));
  }
}

/**
 * Recurses a ProseMirror document, extracting hyperlinks from it in order of
 * appearance. Used to compute the index of the current hyperlink being
 * interacted with, based on keyboard events emitted by the editor.
 * @deprecated
 * @description NOTE: Used only for the purpose of synchronising hyperlink
 * keyboard events with the correct hyperlink in our ECS state. This will be
 * removed once the Editor team provides a more rich hyperlink API.
 */
export function getOrderedLinkPositions(doc: EditorState['doc']) {
  const linkMarks: number[] = [];

  traverseLinks(doc, linkMarks);

  return linkMarks;
}

// Because the type CSSStyleDeclaration['justifyContent'] just expects a string
// and is not as strict in its type definition. Please ensure this is following
// the MDN reference for justifyContent in a flexbox.
type JustifyContentValue = 'start' | 'center' | 'end';
/**
 * Given the current verticalAlignment state for an editor instance, we want to
 * get the justifyContent value that it pertains to because we will be using
 * justifyContent to vertically position/align the contents of the editor (since
 * the editor is represented as a column flexbox).
 * @param verticalAlignment
 * @returns
 */
export function getJustifyContentPropertyFromVerticalAlignment(
  verticalAlignment: VerticalTextAlignment,
): JustifyContentValue {
  switch (verticalAlignment) {
    case VerticalTextAlignment.TOP:
      return 'start';
    case VerticalTextAlignment.MIDDLE:
      return 'center';
    case VerticalTextAlignment.BOTTOM:
      return 'end';
  }
}
